import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Phone, Code, Palette, Zap } from 'lucide-react';
import useMobile from '../hooks/useMobile';

// Code snippet for background effect
const codeSnippet = `
const REAGANS = {
  vision: "Digital Excellence",
  services: [
    "Web Development",
    "AI Solutions", 
    "Business Systems",
    "Custom Software"
  ],
  async buildFuture() {
    return await Promise.all(
      this.services.map(s => 
        createSolution(s)
      )
    );
  }
};

function createSolution(type) {
  const config = {
    quality: "premium",
    design: "modern",
    tech: "cutting-edge"
  };
  return new Solution(type, config);
}

class Solution {
  constructor(type, config) {
    this.type = type;
    this.config = config;
    this.delivered = true;
  }
}

// Transform visions into reality
const results = await REAGANS
  .buildFuture();

console.log("Excellence:", results);
`.trim();

const Home = () => {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const ctaRef = useRef(null);
  const containerRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const isMobile = useMobile();

  const bgY = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["0%", "30%"]);
  const laptopY = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["0%", "20%"]);
  const codeY = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["0%", "40%"]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div data-testid="home-page" className="relative" ref={containerRef}>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Layered Background */}
        <div className="absolute inset-0">
          {/* Layer 1: Laptop/Code Background */}
          <motion.div
            className="absolute inset-0"
            style={{ y: laptopY }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=2000&q=80')`,
                filter: 'brightness(0.4)'
              }}
            />
          </motion.div>

          {/* Layer 2: Code Overlay */}
          <motion.div
            className="absolute inset-0 overflow-hidden opacity-20"
            style={{ y: codeY }}
          >
            <div className="absolute inset-0 flex justify-around">
              {[0, 1, 2].map((col) => (
                <div key={col} className="w-1/3 overflow-hidden">
                  <pre
                    className="text-green-400/60 text-xs font-mono whitespace-pre animate-[codeScroll_30s_linear_infinite]"
                    style={{ animationDelay: `${col * -10}s` }}
                  >
                    {codeSnippet}
                    {'\n\n'}
                    {codeSnippet}
                  </pre>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Layer 3: Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />

          {/* Layer 4: Mesh Gradient */}
          <div className="absolute inset-0 bg-mesh-intense" />

          {/* Layer 5: Noise Texture */}
          <div className="absolute inset-0 noise" />
        </div>

        {/* Decorative Glows - Optimized for Mobile */}
        {!isMobile && (
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-glow/15 rounded-full blur-[128px]"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.15, 0.25, 0.15]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gold/10 rounded-full blur-[100px]"
          animate={isMobile ? {} : {
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Laptop Glow Effect */}
        <div className="laptop-glow top-1/3 left-1/4" />

        {/* Content */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center"
        >


          {/* Main Heading */}
          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-none mb-6"
          >
            <motion.span
              className="text-white inline-block"
              animate={isMobile ? { opacity: 1 } : { opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Turn Your Website Into A
            </motion.span>
            <br />
            <span className="text-gradient-gold">High-Performance Growth Engine</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            Whether you need to replace an underperforming site or launch your first professional presence,
            I build custom platforms that establish authority, capture leads, and drive measurable revenue.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="glass-button px-8 py-4 rounded-full font-mono text-sm uppercase tracking-widest text-gold flex items-center gap-3 group"
              >
                Start Your Project
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.a
              href="tel:+27699512915"
              data-testid="hero-cta-call"
              className="px-8 py-4 rounded-full font-mono text-sm uppercase tracking-widest text-white/70 border border-white/10 hover:border-gold/50 hover:text-gold transition-all duration-300 flex items-center gap-3"
              whileHover={{ scale: 1.05, borderColor: 'rgba(212, 175, 55, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={16} strokeWidth={1.5} />
              Book Strategy Call
            </motion.a>
          </motion.div>
        </motion.div>


      </section>

      {/* Features Preview */}
      <section
        ref={featuresRef}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-background-paper" />
        <div className="absolute inset-0 bg-mesh opacity-50" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Palette,
                title: "Authority & Trust",
                description: "First impressions are everything. I design elite digital experiences that position you as the market leader instantly.",
                delay: 0
              },
              {
                icon: Code,
                title: "Automated Growth",
                description: "Stop chasing leads. I implement AI systems that nurture prospects and book appointments while you sleep.",
                delay: 0.1
              },
              {
                icon: Zap,
                title: "Conversion Focused",
                description: "Traffic means nothing without action. Every pixel is engineered to turn casual visitors into paying clients.",
                delay: 0.2
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.8, delay: feature.delay }}
                data-testid={`feature-card-${index}`}
                className="glass-card p-8 rounded-2xl group cursor-pointer"
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div
                  className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors"
                  whileHover={{ rotate: 5, scale: 1.1 }}
                >
                  <feature.icon size={24} strokeWidth={1.5} className="text-gold" />
                </motion.div>
                <h3 className="font-heading text-xl font-medium text-white mb-3 group-hover:text-gold transition-colors">
                  {feature.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why a Website Section */}
      <section className="relative py-24 md:py-32 bg-background-paper/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
                The Problem
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-medium text-white mb-8">
                Is Your Website (or Lack of One) <span className="text-gradient-gold">Costing You Money?</span>
              </h2>
              <div className="space-y-6 text-white/60">
                <p className="text-lg leading-relaxed">
                  If customers can't find you, you're invisible. If they find you but leave unimpressed, you're losing revenue. I build platforms that capture, convince, and convert—whether you're starting from zero or scaling up.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Invisible to Search",
                    "Lost Credibility",
                    "24/7 Client Acquisition",
                    "Data-Backed Scalability"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="glass-card rounded-3xl p-4 glow-gold overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80"
                  alt="Business Growth Analytics"
                  className="rounded-2xl w-full h-auto opacity-80"
                />
              </div>
              {/* Floating element */}
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-8 -left-8 glass p-6 rounded-2xl hidden md:block"
              >
                <div className="text-gold text-2xl font-bold">+140%</div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">Conversion Increase</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expanded Services Section */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold/60 mb-4 block">
              Our Expertise
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-white">
              Specialized <span className="text-gradient-gold">Digital Services</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "High-Impact Strategy",
                items: ["Zero-to-Launch Guide", "Market Domination", "SEO Mastery", "Conversion Optimization"]
              },
              {
                title: "Custom Development",
                items: ["Scalable Foundations", "React & Next.js", "Secure E-commerce", "Lightning Performance"]
              },
              {
                title: "AI & Automation",
                items: ["Smart Chatbots", "Workflow Automation", "Data Prediction", "Personalized Engines"]
              },
              {
                title: "Peace of Mind",
                items: ["Global Edge Delivery", "Ironclad Security", "Daily Backups", "0% Downtime Guarantee"]
              }
            ].map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass p-8 rounded-3xl hover:border-gold/30 transition-all group"
              >
                <h3 className="text-white text-lg font-medium mb-6 group-hover:text-gold transition-colors underline decoration-gold/0 group-hover:decoration-gold/100 decoration-2 underline-offset-8">{service.title}</h3>
                <ul className="space-y-3">
                  {service.items.map((item, j) => (
                    <li key={j} className="text-white/40 text-xs flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-gold/40" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="relative py-24 md:py-32 overflow-hidden"
      >
        {/* Background with code effect */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=2000&q=80')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-violet-glow/10 via-transparent to-transparent" />
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={ctaInView ? "visible" : "hidden"}
          className="relative max-w-7xl mx-auto px-6 md:px-12 text-center"
        >
          <motion.h2
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="font-heading text-3xl md:text-5xl font-medium text-white mb-6"
          >
            Ready to <span className="text-gradient-gold">Dominate</span> Your Niche?
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-10"
          >
            Your competition isn't waiting. Neither should you.
            Schedule a free strategy call and let's map out your path to market leadership.
          </motion.p>

          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link
                to="/contact"
                data-testid="cta-work-with-me"
                className="inline-flex items-center gap-3 glass-button px-10 py-5 rounded-full font-mono text-sm uppercase tracking-widest text-gold group animate-pulse-glow"
              >
                Claim Your Spot
                <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
